import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spin, Empty, Tabs } from 'antd';
import { ClockCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { getApi, apiConfig } from 'redux/apis';
import axios from 'axios';
import moment from 'moment-timezone';

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

interface HistoryTransactionInfo {
	txnId: string;
	vendorName?: string;
	docNumber?: string;
	txnDate?: string;
	entityType?: string; // needed for inline download endpoint
}

interface DownloadHistoryEntry {
	id: string;
	date: string; // ISO string
	user: string;
	userId: string;
	downloadType: 'selected' | 'all';
	totalAttachments: number;
	message: string;
	fileSizeInBytes: number;
	zipS3Url?: string | null;
	transaction?: HistoryTransactionInfo | null;
	expenseIds?: string[];
}

interface HistoryModalProps {
	visible: boolean;
	onCancel: () => void;
	defaultTab?: 'selected' | 'all';
}

const HistoryModal: React.FC<HistoryModalProps> = ({
	visible,
	onCancel,
	defaultTab = 'selected',
}) => {
	const [history, setHistory] = useState<DownloadHistoryEntry[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

	const [hasMore, setHasMore] = useState<boolean>(false);
	const [nextCursor, setNextCursor] = useState<string>('');
	const [activeTab, setActiveTab] = useState<'selected' | 'all'>(defaultTab);

	const fetchHistory = async (cursor?: string) => {
		try {
			setLoading(true);
			setError(null);
			const companyId = localStorage.getItem('companyId');
			const params: any = { companyId, limit: 30 };
			if (cursor) params.cursorId = cursor;
			const response = await getApi('/attachments/history-recent', params);
			const items: DownloadHistoryEntry[] =
				response?.data?.data ?? response?.data ?? [];
			const merged = cursor ? [...history, ...items] : items;
			setHistory(Array.isArray(merged) ? merged : []);
			console.log('response', response);
			const hasMoreHeader = String(response?.headers?.['X-Has-More'] || '');
			const nextCursorHeader = String(
				response?.headers?.['X-Next-Cursor'] || ''
			);
			setHasMore(hasMoreHeader === 'true');
			setNextCursor(nextCursorHeader);
		} catch (err: any) {
			setError(err?.response?.data?.message || 'Failed to load history');
			if (!cursor) setHistory([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (visible) {
			// reset pagination state when opening
			setHistory([]);
			setHasMore(false);
			setNextCursor('');
			fetchHistory();
		}
	}, [visible]);

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	const formatDateTime = (isoDateString: string): string => {
		if (!isoDateString) return '';
		// Convert from UTC to configured timezone using moment
		const dateInTZ = moment.utc(isoDateString).tz(TIMEZONE);
		const formatted = dateInTZ.format('MM/DD/YYYY, h:mm:ss A');
		return formatted;
	};

	const filteredHistory = useMemo(
		() => history.filter((e) => e.downloadType === activeTab),
		[history, activeTab]
	);

	const endPoint = process.env.REACT_APP_API_ENDPOINT as string;

	const handleDownloadClick = async (entry: DownloadHistoryEntry) => {
		try {
			setDownloadingIds((prev) => new Set(prev).add(entry.id));
			const companyId = localStorage.getItem('companyId');
			const config = await apiConfig();

			// If zipS3Url is available, use the new download-from-s3 endpoint
			if (entry.zipS3Url) {
				const response = await axios.post(
					`${endPoint}/attachments/download-from-s3`,
					{
						s3Url: entry.zipS3Url,
						companyId,
					},
					{
						responseType: 'arraybuffer',
						headers: {
							Authorization: config.headers?.Authorization,
							RefreshToken: config.headers?.RefreshToken,
							'Content-Type': 'application/json',
						},
					}
				);

				const blob = new Blob([response.data], {
					type: response.headers?.['content-type'] || 'application/zip',
				});
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const contentDisposition =
					response.headers?.['content-disposition'] || '';
				const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
				const serverFileName = decodeURIComponent(
					(fileNameMatch?.[1] || '').trim()
				);
				const fallbackName = `attachments_${new Date(entry.date)
					.toISOString()
					.slice(0, 10)}.zip`;
				const finalFileName =
					serverFileName ||
					fallbackName ||
					`attachments_${new Date().toISOString().slice(0, 10)}.zip`;
				link.download = finalFileName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}
			// If this is a individual attachments (selected) history entry without zipS3Url, use inline endpoint
			else if (entry.downloadType === 'selected' && entry.transaction?.txnId) {
				const txnId = entry.transaction.txnId;
				// Use the entityType from the history entry if available, otherwise default to Purchase
				// The backend should populate this field when creating the history entry
				const entityType = entry.transaction.entityType || 'Purchase';
				const response = await axios.post(
					`${endPoint}/attachments/download`,
					{
						companyId,
						transactionIds: [txnId],
						entityType,
					},
					{
						responseType: 'blob',
						headers: {
							Authorization: config.headers?.Authorization,
							RefreshToken: config.headers?.RefreshToken,
							'Content-Type': 'application/json',
						},
					}
				);

				const blob = new Blob([response.data], {
					type: response.headers?.['content-type'] || 'application/zip',
				});
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const disposition = response.headers?.['content-disposition'] || '';
				const match = disposition.match(
					/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/
				);
				const serverFileName = decodeURIComponent(
					(match?.[1] || match?.[2] || '').trim()
				);
				const fallbackName = `txn_${txnId}_${new Date(entry.date)
					.toISOString()
					.slice(0, 10)}.zip`;
				link.download = serverFileName || fallbackName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			} else {
				// Otherwise, use lambda for 'all' or when IDs are aggregated (fallback for entries without zipS3Url)
				const body: any = {
					companyId,
					returnType: 'zip',
					downloadType: 'all',
				};
				// Use expenseIds provided by backend history for exact replay
				body.transactionIds = entry.expenseIds || [];

				const response = await axios.post(
					`${endPoint}/attachments/download-attachment-lambda`,
					body,
					{
						responseType: 'arraybuffer',
						headers: {
							Authorization: config.headers?.Authorization,
							RefreshToken: config.headers?.RefreshToken,
							'Content-Type': 'application/json',
						},
					}
				);

				const blob = new Blob([response.data], {
					type: response.headers?.['content-type'] || 'application/zip',
				});
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const contentDisposition =
					response.headers?.['content-disposition'] || '';
				const fileNameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
				const serverFileName = decodeURIComponent(
					(fileNameMatch?.[1] || '').trim()
				);
				const fallbackName = `attachments_${new Date(entry.date)
					.toISOString()
					.slice(0, 10)}.zip`;
				const finalFileName =
					serverFileName ||
					fallbackName ||
					`attachments_${new Date().toISOString().slice(0, 10)}.zip`;
				link.download = finalFileName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}
		} catch (e) {
			console.error('Download failed:', e);
		} finally {
			setDownloadingIds((prev) => {
				const next = new Set(prev);
				next.delete(entry.id);
				return next;
			});
		}
	};

	return (
		<Modal
			title={
				<div className={styles['modal-title']}>
					<p className={styles['modal-title']}>Download History</p>
					<Tabs
						size="small"
						activeKey={activeTab}
						onChange={(k) => setActiveTab(k as 'selected' | 'all')}
						items={[
							{ key: 'selected', label: 'Individual attachments' },
							{ key: 'all', label: 'All' },
						]}
					/>
				</div>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			className={styles['history-modal']}
			width={500}
			centered
			closable={true}
			maskClosable={false}
		>
			<div className={styles['modal-content']}>
				<p className={styles['modal-description']}>
					View your recent attachment download history.
				</p>

				{loading ? (
					<div className={styles['loading-container']}>
						<Spin />
					</div>
				) : error ? (
					<div className={styles['loading-container']}>
						<Empty description={error} />
					</div>
				) : history.length === 0 ? (
					<div className={styles['loading-container']}>
						<Empty description="No recent downloads" />
					</div>
				) : (
					<div className={styles['history-list']}>
						{filteredHistory.map((entry) => (
							<div key={entry.id} className={styles['history-item']}>
								<div className={styles['history-content']}>
									<div className={styles['history-header']}>
										<ClockCircleOutlined
											className={styles['status-icon-default']}
										/>
										<div className={styles['history-summary']}>
											<span className={styles['file-count']}>
												{entry.totalAttachments} attachment
												{entry.totalAttachments > 1 ? 's' : ''}
											</span>
											<span
												className={`${styles['badge']} ${
													entry.downloadType === 'selected'
														? styles['badge-selected']
														: styles['badge-all']
												}`}
											>
												{entry.downloadType === 'selected'
													? 'Individual attachments'
													: 'All'}
											</span>
										</div>
									</div>
									<div className={styles['meta-row']}>
										<span className={styles['timestamp']}>
											{formatDateTime(entry.date)}
										</span>
										<div className={styles['file-info']}>
											<FileTextOutlined className={styles['file-icon']} />
											<span className={styles['file-size']}>
												{formatFileSize(entry.fileSizeInBytes || 0)}
											</span>
										</div>
									</div>
									<div className={styles['message']}>{entry.message}</div>
									<div className={styles['history-details']}>
										<span className={styles['timestamp']}>{entry.user}</span>
									</div>
									<div>
										<Button
											type="link"
											onClick={() => handleDownloadClick(entry)}
											disabled={downloadingIds.has(entry.id)}
											loading={downloadingIds.has(entry.id)}
										>
											{downloadingIds.has(entry.id)
												? 'Downloading...'
												: 'Download ZIP'}
										</Button>
									</div>

									{entry.downloadType === 'selected' && entry.transaction ? (
										<div className={styles['txn-chips']}>
											<span className={styles['chip']}>
												Txn {entry.transaction.txnId}
											</span>
											{entry.transaction.vendorName ? (
												<span className={styles['chip']}>
													{entry.transaction.vendorName}
												</span>
											) : null}
											{/* {entry.transaction.docNumber ? (
												<span className={styles['chip']}>
													{entry.transaction.docNumber}
												</span>
											) : null} */}
											{entry.transaction.txnDate ? (
												<span className={styles['chip']}>
													{/* {entry.transaction.txnDate}.format('MM/DD/YYYY') */}
													{moment(entry.transaction.txnDate).format('MM/DD/YYYY')}
												</span>
											) : null}
										</div>
									) : null}
								</div>
							</div>
						))}
					</div>
				)}

				<div className={styles['history-footer']}>
					<span className={styles['footer-text']}>
						Showing
						{activeTab === 'selected' ? ' transaction attachment' : ' all'}{' '}
						{filteredHistory.length} downloads
						{hasMore ? ' (more available)' : ''}.
					</span>
					{hasMore ? (
						<Button
							size="small"
							onClick={() => fetchHistory(nextCursor)}
							disabled={loading}
						>
							{loading ? 'Loading…' : 'Load more'}
						</Button>
					) : null}
				</div>

				<div className={styles['modal-actions']}>
					<Button onClick={onCancel} className={styles['close-button']}>
						Close
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default HistoryModal;
